import { motion } from 'motion/react';
import { useT } from '@/shared/lib/i18n';
import { Button, DossierFolder, EvidenceTape, HandKicker, PhaseTag, Pin, Stamp } from '@/shared/ui';
import { DistributionHeader } from './DistributionHeader';

export type PassDeviceCardProps = {
  playerName: string;
  /** 1-based step in the round (e.g. 1 of 5). */
  step: number;
  total: number;
  fileNumber: number;
  onReady: () => void;
};

/**
 * Pass-device hand-off shown before the dossier reveal. Distribution
 * header (PhaseHeader + TOUR post-it + step dots) on top, then a
 * manila dossier folder with the recipient's name on a paper card
 * inside, NE PAS REGARDER evidence tape, and an orange CTA to open
 * the dossier. The same `<DistributionHeader>` carries over into
 * `<DossierReveal>` so round/turn context never disappears.
 */
export const PassDeviceCard = ({
  playerName,
  step,
  total,
  fileNumber,
  onReady,
}: PassDeviceCardProps) => {
  const t = useT();
  const dossier = t('play.pass_device.dossier', {
    n: String(step).padStart(2, '0'),
    total: String(total).padStart(2, '0'),
  });

  return (
    <main className="relative flex h-full w-full flex-col items-stretch justify-between gap-4 px-4 py-4 sm:px-8">
      <DistributionHeader step={step} total={total} />

      <div className="flex flex-1 items-center justify-center">
        <motion.div
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="relative w-full max-w-[380px]"
        >
          <DossierFolder
            tabLabel={dossier}
            stamp={
              <Stamp tone="red" rotate={8} className="px-4 py-1.5 text-[15px] tracking-[0.16em]">
                CONFIDENTIEL
              </Stamp>
            }
          >
            <div className="paper-dossier relative mx-1 mt-8 mb-1 px-5 py-7 text-center">
              <Pin left={20} top={-4} />
              <Pin left={'calc(100% - 20px)'} top={-4} />
              <PhaseTag size={10} tracking={0.28}>
                ✦ {t('play.pass_device.eyebrow')} ✦
              </PhaseTag>
              <div className="mt-3 font-display font-extrabold text-[44px] text-ink capitalize leading-[0.95] sm:text-[56px]">
                {playerName}
              </div>
              <HandKicker size={16} color="var(--color-magenta)" rot={-1} className="mt-3">
                {t('play.pass_device.no_peeking')}
              </HandKicker>
              <div className="mt-4 flex justify-between border-ink/20 border-t border-dashed pt-2 font-type text-[10px] text-ink/45 tracking-[0.18em]">
                <span>FILE · {String(fileNumber).padStart(3, '0')}</span>
                <span>EYES ONLY</span>
              </div>
            </div>
          </DossierFolder>
        </motion.div>
      </div>

      <div className="relative w-full overflow-hidden">
        <div className="relative -mx-4 h-9 sm:-mx-8">
          <EvidenceTape
            left={-8}
            right={-8}
            top={0}
            height={36}
            rot={-2}
            label={t('round.distribution.no_peeking_tape')}
            labelSize={12}
            labelHeight={24}
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[360px]">
        <Button variant="orange" onClick={onReady}>
          {t('play.pass_device.ready')}
        </Button>
      </div>
    </main>
  );
};
